import type {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import {Box, InputAdornment, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import InputElement from '@jobseeker/lotus/elements/Input';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {RupeeGreen, RupeeOrange} from '@jobseeker/lotus/icons';

export type SalaryInputUnit = 'lakhs' | 'crores';

export type SalaryInputVisualTheme = {
  toggleBg: string;
  activeBg: string;
  activeShadow: string;
  previewBg: string;
  previewBorder: string;
  accent: string;
  prefix: string;
  text: string;
  focusRing: string;
  inputFocusBorder: string;
  rupeeIcon: string;
};

export type SalaryInputProps = {
  name: string;
  value: string;
  unit: SalaryInputUnit;
  placeholder: string;
  salaryTheme: SalaryInputVisualTheme;
  error?: boolean;
  helperText?: string;
  previewText?: string;
  previewSuffix?: string;
  showPreviewPrefix?: boolean;
  maxLength?: number;
  reservePreviewSpace?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onPaste?: ClipboardEventHandler<HTMLDivElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onUnitChange: (unit: SalaryInputUnit) => void;
};

const SALARY_PREVIEW_ICON_SIZE = 22;

export const IIMJOBS_SALARY_INPUT_THEME: SalaryInputVisualTheme = {
  toggleBg: '#F3F7FA',
  activeBg: '#FFFFFF',
  activeShadow: '0px 0px 5px rgba(0, 0, 0, 0.10)',
  previewBg: '#F6FFFA',
  previewBorder: '#AFE1C7',
  accent: '#49966E',
  prefix: '#7E7E7E',
  text: '#333333',
  focusRing: 'rgba(51, 51, 51, 0.35)',
  inputFocusBorder: '#33333390',
  rupeeIcon: RupeeGreen,
};

export const getSalaryInputVisualTheme = ({
  theme,
  isAppIimjobs,
}: {
  theme: any;
  isAppIimjobs: boolean;
}): SalaryInputVisualTheme => {
  if (isAppIimjobs) {
    return IIMJOBS_SALARY_INPUT_THEME;
  }

  const hiristPrimary = theme.palette?.primary?.main ?? '#FF6A3D';

  return {
    ...IIMJOBS_SALARY_INPUT_THEME,
    previewBg: '#fff9f7',
    previewBorder: hiristPrimary,
    accent: '#373737',
    focusRing: hiristPrimary,
    inputFocusBorder: hiristPrimary,
    rupeeIcon: RupeeOrange,
  };
};

const SalaryUnitToggle = ({
  unit,
  onChange,
  salaryTheme,
}: {
  unit: SalaryInputUnit;
  onChange: (unit: SalaryInputUnit) => void;
  salaryTheme: SalaryInputVisualTheme;
}) => {
  const selectedPill = {
    left: unit === 'lakhs' ? 4 : 54,
    width: unit === 'lakhs' ? 50 : 56,
  };
  const options: {label: string; value: SalaryInputUnit; width: number}[] = [
    {label: 'Lakhs', value: 'lakhs', width: 50},
    {label: 'Crores', value: 'crores', width: 56},
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: salaryTheme.toggleBg,
        borderRadius: '46px',
        display: 'flex',
        alignItems: 'center',
        height: 30,
        width: 114,
        p: '4px',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          left: `${selectedPill.left}px`,
          width: selectedPill.width,
          height: 22,
          bgcolor: salaryTheme.activeBg,
          borderRadius: '32px',
          boxShadow: salaryTheme.activeShadow,
          transition:
            'left 180ms cubic-bezier(0.4, 0, 0.2, 1), width 180ms cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none',
        }}
      />
      {options.map(option => {
        return (
          <Box
            key={option.value}
            role="button"
            tabIndex={0}
            onMouseDown={(event: MouseEvent) => event.preventDefault()}
            onClick={() => onChange(option.value)}
            onKeyDown={(event: KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onChange(option.value);
              }
            }}
            sx={{
              position: 'relative',
              zIndex: 1,
              width: option.width,
              height: 22,
              color: salaryTheme.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'inherit',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: 1.2,
              outline: 'none',
              p: 0,
              transition: 'color 180ms cubic-bezier(0.4, 0, 0.2, 1)',
              '&:focus-visible': {
                boxShadow: `inset 0 0 0 1px ${salaryTheme.focusRing}`,
                borderRadius: '32px',
              },
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
};

const SalaryPreview = ({
  text,
  showPrefix,
  previewSuffix,
  salaryTheme,
}: {
  text: string;
  showPrefix?: boolean;
  previewSuffix: string;
  salaryTheme: SalaryInputVisualTheme;
}) => {
  const amountText = text.endsWith(previewSuffix)
    ? text.slice(0, -previewSuffix.length)
    : text;

  return (
    <Box
      sx={{
        width: '100%',
        // minHeight: showPrefix ? 50 : 54,
        minHeight: '42px',
        mt: '10px',
        bgcolor: salaryTheme.previewBg,
        // border: `1px solid ${salaryTheme.previewBorder}`,
        borderRadius: showPrefix ? '10px' : '12px',
        display: 'flex',
        alignItems: 'center',
        px: '15px',
        py: showPrefix ? '8px' : '10px',
      }}
    >
      <Box>
        {showPrefix && (
          <Typography
            sx={{
              color: salaryTheme.prefix,
              fontSize: '10px',
              fontWeight: 400,
              lineHeight: '16px',
            }}
          >
            You&apos;ve entered:
          </Typography>
        )}
        <Typography
          sx={{
            color: salaryTheme.accent,
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '16px',
          }}
        >
          <Box component="span">{amountText}</Box>
          <Box component="span" sx={{fontWeight: 400}}>
            {previewSuffix}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

const SalaryInput = ({
  name,
  value,
  unit,
  placeholder,
  salaryTheme,
  error = false,
  helperText = '',
  previewText = '',
  previewSuffix = ' per annum',
  showPreviewPrefix = false,
  maxLength = 6,
  reservePreviewSpace = false,
  onFocus,
  onChange,
  onPaste,
  onBlur,
  onUnitChange,
}: SalaryInputProps) => {
  return (
    <>
      <InputElement
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onFocus={onFocus}
        onChange={onChange}
        onPaste={onPaste}
        onBlur={onBlur}
        InputProps={{
          inputProps: {
            inputMode: 'decimal',
            maxLength,
          },
          endAdornment: (
            <InputAdornment position="end">
              <SalaryUnitToggle
                unit={unit}
                salaryTheme={salaryTheme}
                onChange={onUnitChange}
              />
            </InputAdornment>
          ),
        }}
        error={error}
        helperText={helperText}
        variant={VersionVariants.VERSION_TWO}
        sx={{
          width: '100%',
          height: '40px',
          mt: 0,
          '& .MuiFormHelperText-root': {
            color: error ? 'error.main' : '#7E7E7E',
          },
          '& .MuiOutlinedInput-root': {
            height: '40px',
            borderRadius: '32px',
            pl: 0,
            pr: '4px',
            '& fieldset': {
              borderColor: 'rgba(126, 126, 126, 0.20)',
            },
            '&:hover fieldset': {
              borderColor: salaryTheme.inputFocusBorder,
            },
            '&.Mui-focused fieldset': {
              borderColor: salaryTheme.inputFocusBorder,
              borderWidth: '1px',
            },
          },
          '& .MuiInputBase-input': {
            color: '#333333',
            fontFamily: 'Poppins',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '16px',
            padding: '10px 0 10px 18px',
          },
          '& .MuiInputAdornment-root': {
            height: '30px',
            maxHeight: 'none',
            ml: '8px',
            mr: 0,
          },
        }}
      />

      {reservePreviewSpace ? (
        <Box
          sx={{
            mt: '10px',
            minHeight: '42px',
            visibility: !error && !helperText && previewText ? 'visible' : 'hidden',
          }}
        >
          {previewText && (
            <SalaryPreview
              text={previewText}
              showPrefix={showPreviewPrefix}
              previewSuffix={previewSuffix}
              salaryTheme={salaryTheme}
            />
          )}
        </Box>
      ) : (
        !error && !helperText && previewText && (
          <SalaryPreview
            text={previewText}
            showPrefix={showPreviewPrefix}
            previewSuffix={previewSuffix}
            salaryTheme={salaryTheme}
          />
        )
      )}
    </>
  );
};

export default SalaryInput;
